import { combineReducers, configureStore } from "@reduxjs/toolkit";
import type { Immutable } from "immer";
import React from "react";
import { Provider } from "react-redux";
import { BillingAddress } from "tsi-common-react/src/apps/checkout/BillingAddress/index";
import { PaymentMethods } from "tsi-common-react/src/apps/checkout/PaymentMethods/index";
import { ShippingAddress } from "tsi-common-react/src/apps/checkout/ShippingAddress/index";
import { Step } from "tsi-common-react/src/apps/checkout/constants";
import { defaults } from "tsi-common-react/src/apps/checkout/defaults";
import { reducers as checkoutReducers } from "tsi-common-react/src/apps/checkout/reducers";
import type { IReduxState } from "tsi-common-react/src/apps/checkout/reducers.interfaces";
import { reducers as commonReducers } from "tsi-common-react/src/store/common/reducers";

import { templates } from "@reactivated";

import { StyleguideChrome } from "../components/StyleguideChrome";

type IProps = Immutable<templates.StyleguideCheckoutStepElementTemplate>;

const buildCheckoutStore = (checkoutJson: string, step: Step) =>
    configureStore({
        reducer: combineReducers({
            checkout: checkoutReducers,
            common: commonReducers,
        }),
        preloadedState: {
            checkout: (() => {
                const overrides = JSON.parse(
                    checkoutJson,
                ) as Partial<IReduxState>;
                return {
                    ...defaults,
                    data: { ...defaults.data, ...overrides.data },
                    form: {
                        ...defaults.form,
                        ...overrides.form,
                        current_step: step,
                    },
                    derived: { ...defaults.derived, ...overrides.derived },
                    gifts: { ...defaults.gifts, ...overrides.gifts },
                };
            })(),
        },
    });

const StepWrapper = (props: {
    children: React.ReactNode;
}): React.JSX.Element => <main id="checkout-app">{props.children}</main>;

const ShippingStepFixture = (props: {
    checkoutJson: string;
}): React.JSX.Element => {
    const store = React.useMemo(
        () => buildCheckoutStore(props.checkoutJson, Step.SHIPPING_ADDRESS),
        [props.checkoutJson],
    );
    return (
        <Provider store={store}>
            <StepWrapper>
                <ShippingAddress
                    heading="1. Shipping Address"
                    errors={[]}
                    isCurrentStep={true}
                    saveEmailAddress={() =>
                        Promise.resolve({ owner_email: "" })
                    }
                    saveShippingAddress={() => Promise.reject()}
                    onContinue={() => undefined}
                    onEdit={() => undefined}
                />
            </StepWrapper>
        </Provider>
    );
};

const BillingStepFixture = (props: {
    checkoutJson: string;
}): React.JSX.Element => {
    const store = React.useMemo(
        () => buildCheckoutStore(props.checkoutJson, Step.BILLING_ADDRESS),
        [props.checkoutJson],
    );
    return (
        <Provider store={store}>
            <StepWrapper>
                <BillingAddress
                    heading="3. Billing Address"
                    errors={[]}
                    isCurrentStep={true}
                    saveBillingAddress={() => Promise.reject()}
                    onContinue={() => undefined}
                    onEdit={() => undefined}
                />
            </StepWrapper>
        </Provider>
    );
};

const PaymentStepFixture = (props: {
    checkoutJson: string;
}): React.JSX.Element => {
    const store = React.useMemo(
        () => buildCheckoutStore(props.checkoutJson, Step.PAYMENT_METHODS),
        [props.checkoutJson],
    );
    return (
        <Provider store={store}>
            <StepWrapper>
                <PaymentMethods
                    heading="4. Payment"
                    errors={[]}
                    buildFinancingUpsell={() => null}
                    enableSplitPay={false}
                    onContinue={() => undefined}
                    isCurrentStep={true}
                />
            </StepWrapper>
        </Provider>
    );
};

const STEP_COMPONENTS: Record<
    string,
    (props: { checkoutJson: string }) => React.JSX.Element
> = {
    shipping: ShippingStepFixture,
    billing: BillingStepFixture,
    payment: PaymentStepFixture,
};

export const Template = (props: IProps): React.JSX.Element => {
    const StepComponent = STEP_COMPONENTS[props.step];
    if (!StepComponent) {
        return (
            <StyleguideChrome storybook={props.storybook}>
                <div>Unknown checkout step: {props.step}</div>
            </StyleguideChrome>
        );
    }
    const content = <StepComponent checkoutJson={props.checkout_json} />;
    return (
        <StyleguideChrome storybook={props.storybook}>
            {props.storybook ? (
                content
            ) : (
                <div id="element-tester">{content}</div>
            )}
        </StyleguideChrome>
    );
};
